import { DownloadSimpleIcon, CheckIcon, PlusIcon, XIcon, TrayIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import deepEq from 'fast-deep-equal';
import { BusinessUnit as GraphqlBusinessUnit } from 'src/types/enums';

import { Button } from '../../../components/button/Button';
import { ISimpleComboboxItem } from '../../../components/combobox/SimpleCombobox';
import { Input } from '../../../components/input/Input';
import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { SearchType, SearchWithType } from '../../../components/search-with-type/SearchWithType';
import { SimpleSelect } from '../../../components/select/SimpleSelect';
import { Pill } from '../../../components/pill/Pill';
import { ITableHeader, Table } from '../../../components/table/Table';
import {
  OrderDateFilterType,
  OrderDocumentValidationStatus,
  OrderLineStopType,
  OrderStatus,
  SaleStatus,
  PurchaseStatus,
} from 'src/types/enums';
import { InternalRole } from '../../auth/endpoints/MeEndpoint.types';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { formatDate, formatInputTime } from '../../../utils/date';
import { decimateAndFormatNumber, parseIntOrNull } from '../../../utils/number';
import {
  BUSINESS_UNIT_OPTIONS,
  graphqlBusinessUnitToNativeBusinessUnit,
  nativeBusinessUnitToGraphqlBusinessUnit,
  ORDER_STATUS_COLOR,
  ORDER_STATUS_OPTIONS,
  PURCHASE_STATUS_COLOR,
  PURCHASE_STATUS_OPTIONS,
  SALE_STATUS_COLOR,
  SALE_STATUS_OPTIONS,
} from '../constants';
import { calculateLinesTotalExclVat } from '../utils/price';
import { getDisplayError } from '../../../utils/get-display-error';
import { downloadUrl } from '../../../utils/download';
import { useSearchParam } from '../../../hooks/useSearchParam';
import { useAuth } from '../../../contexts/auth-context';
import { OrderLocationCombobox, ComboValue as LocationComboValue } from '../components/OrderLocationCombobox';
import { DOCUMENT_FILTER_KEYS, DocumentFilterSelect } from '../components/DocumentFilterSelect';
import { LinkButton } from '../../../components/button/ButtonLink';
import { PageHeading } from 'components/PageHeading';
import { Breadcrumb } from 'components/Breadcrumb';
import { FilterField, FilterPanel, ListPage } from 'components/page-templates/ListPage';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../utils/track-event';
import { sleep } from '@utils/sleep';
import { roleToNumber } from 'src/app/auth/roles.client';
import dayjs from 'dayjs';
import { fetchMinimalOrders, ordersExportStatus, startOrdersExport } from '../models';
import type { BusinessUnit } from '../enums';
import type { OrdersFilterQuery } from '../endpoints/utils';

const ORDERS_HEADERS: ITableHeader[] = [
  {
    id: 'id',
    name: 'Order Nr',
  },
  {
    id: 'customer',
    name: 'Klant',
    width: '200px',
  },
  {
    id: 'customerRef',
    name: 'Klantref',
  },
  {
    id: 'loadLocation',
    name: 'Laadplaats',
  },
  {
    id: 'unloadLocation',
    name: 'Bestemming',
  },
  {
    id: 'purchaseAmount',
    name: 'Aankooptotaal',
  },
  {
    id: 'saleAmount',
    name: 'Facturatietotaal',
  },
  {
    id: 'status',
    name: 'Status',
  },
  {
    id: 'purchaseStatus',
    name: 'AK Status',
  },
  {
    id: 'saleStatus',
    name: 'VK Status',
  },
  {
    id: 'docsStatus',
    name: 'Docs',
  },
  {
    id: 'loadDate',
    name: 'Laaddatum',
  },
  {
    id: 'loadTime',
    name: 'Laaduur',
  },
  {
    id: 'unloadDate',
    name: 'Losdatum',
  },
  {
    id: 'unloadTime',
    name: 'Losuur',
  },
  {
    id: 'supplier',
    name: 'Vervoerder',
    width: '200px',
  },
  {
    id: 'carrierType',
    name: 'Type carrier',
  },
];

const searchTypes: SearchType[] = [
  {
    label: 'Order referentie',
    value: 'orderNumber',
  },
  {
    label: 'Klant naam',
    value: 'customerName',
  },
  {
    label: 'Factuurreferentie klant',
    value: 'customerRef',
  },
  {
    label: 'Vervoerder naam',
    value: 'supplierName',
  },
  {
    label: 'Laad referentie',
    value: 'loadRef',
  },
  {
    label: 'Los referentie',
    value: 'unloadRef',
  },
];

const DATE_TYPE_OPTIONS = [
  {
    key: OrderDateFilterType.Unload,
    name: 'Losdatum',
  },
  {
    key: OrderDateFilterType.Load,
    name: 'Laaddatum',
  },
  {
    key: OrderDateFilterType.Creation,
    name: 'Ingave datum',
  },
];

const CARRIER_TYPE_OPTIONS = [
  {
    key: 'vast',
    name: 'Vast',
  },
  {
    key: 'spot',
    name: 'Spot',
  },
];

// Selected document conditions ("has-x" / "no-x" per type, combined with AND).
// Deserializer also accepts the legacy single-select shape { key, name } once
// stored under this key, so existing bookmarks keep working.
const deserializeDocFilters = (val: string): string[] => {
  try {
    const parsed = JSON.parse(val);
    if (Array.isArray(parsed)) {
      return parsed.filter((k): k is string => typeof k === 'string' && DOCUMENT_FILTER_KEYS.has(k));
    }
    if (parsed && typeof parsed.key === 'string') {
      return DOCUMENT_FILTER_KEYS.has(parsed.key) ? [parsed.key] : [];
    }
    return [];
  } catch {
    return [];
  }
};

enum FilterType {
  StartDate = 'start-date',
  EndDate = 'end-date',
  SearchOne = 'search-one',
  SearchTwo = 'search-two',
  OrderStatus = 'order-status',
  SaleStatus = 'sale-status',
  PurchaseStatus = 'purchase-status',
  DateType = 'date-type',
  CmrFilter = 'cmr-filter',
  LoadLocation = 'load-location',
  UnloadLocation = 'unload-location',
  BusinessUnit = 'bu',
  CarrierType = 'carrier-type',
}

const defaultStartDate = dayjs().subtract(1, 'month').format('YYYY-MM-DD');

const OrdersPage = () => {
  const { me } = useAuth();
  const [_searchParams, setSearchParams] = useSearchParams();
  const [isExporting, setIsExporting] = useState(false);

  // Filter values
  const [searchOne, setSearchOne] = useSearchParam(FilterType.SearchOne, {
    value: '',
    type: searchTypes[0].value,
  });
  const [searchTwo, setSearchTwo] = useSearchParam(FilterType.SearchTwo, {
    value: '',
    type: searchTypes[1].value,
  });
  const [startDate, setStartDate] = useSearchParam(FilterType.StartDate, defaultStartDate);
  const [endDate, setEndDate] = useSearchParam(FilterType.EndDate, '');
  const [selectedDateType, setSelectedDateType] = useSearchParam<ISimpleComboboxItem>(
    FilterType.DateType,
    DATE_TYPE_OPTIONS[0],
  );
  const [selectedOrderStatus, setSelectedOrderStatus] = useSearchParam<ISimpleComboboxItem | null>(
    FilterType.OrderStatus,
    null,
  );
  const [selectedSaleStatus, setSelectedSaleStatus] = useSearchParam<ISimpleComboboxItem | null>(
    FilterType.SaleStatus,
    null,
  );
  const [selectedPurchaseStatus, setSelectedPurchaseStatus] = useSearchParam<ISimpleComboboxItem | null>(
    FilterType.PurchaseStatus,
    null,
  );
  const [selectedDocFilters, setSelectedDocFilters] = useSearchParam<string[]>(
    FilterType.CmrFilter,
    [],
    JSON.stringify,
    deserializeDocFilters,
  );
  const [loadLocationFilter, setLoadLocationFilter] = useSearchParam<LocationComboValue | null>(
    FilterType.LoadLocation,
    null,
  );
  const [unloadLocationFilter, setUnloadLocationFilter] = useSearchParam<LocationComboValue | null>(
    FilterType.UnloadLocation,
    null,
  );
  const [businessUnitFilter, setBusinessUnitFilter] = useSearchParam<BusinessUnit | null>(
    FilterType.BusinessUnit,
    null,
  );
  const [selectedCarrierType, setSelectedCarrierType] = useSearchParam<ISimpleComboboxItem | null>(
    FilterType.CarrierType,
    null,
  );

  const getFiltersValue = () => {
    const docFilters = new Set(selectedDocFilters);
    const filters: OrdersFilterQuery = {
      dateType: selectedDateType.key as OrderDateFilterType,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      status: selectedOrderStatus?.key as OrderStatus | undefined,
      saleStatus: selectedSaleStatus?.key as SaleStatus | undefined,
      purchaseStatus: selectedPurchaseStatus?.key as PurchaseStatus | undefined,
      hasCMR: docFilters.has('has-cmr') ? true : docFilters.has('no-cmr') ? false : undefined,
      hasDeliveryNote: docFilters.has('has-delivery-note')
        ? true
        : docFilters.has('no-delivery-note')
          ? false
          : undefined,
      hasWeighSlip: docFilters.has('has-weigh-slip') ? true : docFilters.has('no-weigh-slip') ? false : undefined,
      hasPalletNote: docFilters.has('has-pallet-note') ? true : docFilters.has('no-pallet-note') ? false : undefined,
      hasAnnexII: docFilters.has('has-annex-ii') ? true : docFilters.has('no-annex-ii') ? false : undefined,
      hasT1Document: docFilters.has('has-t1-document') ? true : docFilters.has('no-t1-document') ? false : undefined,
      hasAnnexVII: docFilters.has('has-annex-vii') ? true : docFilters.has('no-annex-vii') ? false : undefined,
      loadLocationCountry: loadLocationFilter?.country,
      loadLocationPostalCode: loadLocationFilter?.postalCode,
      unloadLocationCountry: unloadLocationFilter?.country,
      unloadLocationPostalCode: unloadLocationFilter?.postalCode,
      businessUnit: businessUnitFilter,
      carrierType: (selectedCarrierType?.key as 'vast' | 'spot' | undefined) ?? undefined,
    };

    const setSearchFilter = (value: string, type: string) => {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        return;
      }

      switch (type) {
        case 'customerId':
          filters.customerId = parseIntOrNull(trimmedValue) ?? undefined;
          break;
        case 'customerName':
          filters.customerName = trimmedValue;
          break;
        case 'orderNumber':
          filters.orderNumber = trimmedValue;
          break;
        case 'customerRef':
          filters.customerRef = trimmedValue;
          break;
        case 'supplierName':
          filters.supplierName = trimmedValue;
          break;
        case 'loadRef':
          filters.loadRef = trimmedValue;
          break;
        case 'unloadRef':
          filters.unloadRef = trimmedValue;
          break;
      }
    };

    if (searchOne.value) {
      setSearchFilter(searchOne.value, searchOne.type);
    }

    if (searchTwo.value) {
      setSearchFilter(searchTwo.value, searchTwo.type);
    }

    return filters;
  };

  const [filters, setFilters] = useState<OrdersFilterQuery>(() => getFiltersValue());
  const [totalOrderCount, setTotalOrderCount] = useState(0);
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      const result = await fetchMinimalOrders({
        id: variables.cursor,
        take: variables.take,
        filters,
        orderBy: {
          orderNumber: 'desc',
        },
      });

      setTotalOrderCount(result.totalCount ?? 0);

      return result.orders ?? [];
    },
    [filters, setTotalOrderCount],
  );
  const page = usePagination({
    key: 'id',
    pageSize: 50,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  useEffect(() => {
    page.reset();
  }, [filters]);

  useEffect(() => {
    const timeoutRef = setTimeout(() => {
      const newFilters = getFiltersValue();
      setFilters((prev) => {
        if (deepEq(prev, newFilters)) {
          return prev;
        } else {
          return newFilters;
        }
      });
    }, 100);

    return () => clearTimeout(timeoutRef);
  }, [
    searchOne,
    searchTwo,
    startDate,
    endDate,
    selectedDateType,
    selectedOrderStatus,
    selectedSaleStatus,
    selectedPurchaseStatus,
    selectedDocFilters,
    loadLocationFilter,
    unloadLocationFilter,
    businessUnitFilter,
    selectedCarrierType,
  ]);

  const isAdmin = roleToNumber(me.role) >= roleToNumber(InternalRole.InternalAdmin);

  const activeFilterCount = [
    searchOne.value.trim(),
    searchTwo.value.trim(),
    startDate,
    endDate,
    selectedOrderStatus,
    selectedSaleStatus,
    selectedPurchaseStatus,
    selectedDocFilters.length,
    loadLocationFilter,
    unloadLocationFilter,
    businessUnitFilter,
    selectedCarrierType,
  ].filter(Boolean).length;

  const searchOneLabel = searchTypes.find((t) => t.value === searchOne.type)?.label ?? 'Zoeken';
  const searchTwoLabel = searchTypes.find((t) => t.value === searchTwo.type)?.label ?? 'Zoeken';

  return (
    <>
      <PageHeader title="Orders" />

      <ListPage
        heading={
          <PageHeading
            noMarginBottom
            leftSide={<Breadcrumb currentItem="Orders" />}
            rightSide={
              <>
                <LinkButton to="incoming" iconLeft={<TrayIcon className="button-icon" />}>
                  Auto-verwerking
                </LinkButton>
                {isAdmin && (
                  <Button
                    onTrigger={async () => {
                      setIsExporting(true);
                      try {
                        trackEvent('orders_export_started', {
                          filter_count: Object.keys(filters ?? {}).length,
                        });
                        const res = await startOrdersExport({
                          filters,
                        });

                        const jobId = res.jobId;
                        if (!jobId) {
                          throw new Error('Order export did not return a valid job id');
                        }

                        while (true) {
                          console.log('Pinging for export status');
                          const status = await ordersExportStatus(jobId);
                          const url = status.url;
                          if (url) {
                            toast.success('Orders export aangemaakt, wordt nu gedownload');
                            downloadUrl(url, {
                              filename: 'orders.xlsx',
                              openInNewTab: true,
                            });
                            break;
                          }

                          await sleep(2000);
                        }
                      } catch (err) {
                        captureException(err);
                        toast.error('Order export mislukt: ' + getDisplayError(err));
                      }
                      setIsExporting(false);
                    }}
                    isLoading={isExporting}
                    iconLeft={<DownloadSimpleIcon className="button-icon" />}
                  >
                    Export Excel
                  </Button>
                )}
                <LinkButton to="new-from-template" iconLeft={<PlusIcon className="button-icon" />}>
                  Nieuwe orders
                </LinkButton>
                <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
                  Nieuwe order
                </LinkButton>
              </>
            }
          />
        }
        filters={
          <FilterPanel
            activeCount={activeFilterCount}
            actions={
              <Button
                size={8}
                color="secondary"
                iconLeft={<XIcon className="button-icon" />}
                onTrigger={() => setSearchParams({})}
              >
                Wis alle filters
              </Button>
            }
          >
            <FilterField label={searchOneLabel}>
              <SearchWithType
                value={searchOne.value}
                onChange={(val) => setSearchOne((prev) => ({ ...prev, value: val }))}
                types={searchTypes}
                selectedType={searchOne.type}
                onTypeChange={(val) => setSearchOne((prev) => ({ ...prev, type: val }))}
              />
            </FilterField>

            <FilterField label={searchTwoLabel}>
              <SearchWithType
                value={searchTwo.value}
                onChange={(val) => setSearchTwo((prev) => ({ ...prev, value: val }))}
                types={searchTypes}
                selectedType={searchTwo.type}
                onTypeChange={(val) => setSearchTwo((prev) => ({ ...prev, type: val }))}
              />
            </FilterField>

            <FilterField label="Business unit">
              <SimpleSelect
                onSelect={(value) => {
                  if (!value) {
                    setBusinessUnitFilter(null);
                  } else {
                    setBusinessUnitFilter(graphqlBusinessUnitToNativeBusinessUnit(value.key as GraphqlBusinessUnit));
                  }
                }}
                selectedItem={
                  BUSINESS_UNIT_OPTIONS.find((v) =>
                    filters?.businessUnit != null
                      ? v.key === nativeBusinessUnitToGraphqlBusinessUnit(filters.businessUnit)
                      : false,
                  ) ?? null
                }
                items={BUSINESS_UNIT_OPTIONS}
                isOptional={true}
              />
            </FilterField>

            <FilterField label="Type datum">
              <SimpleSelect
                selectedItem={selectedDateType}
                items={[...DATE_TYPE_OPTIONS]}
                onSelect={(val) => {
                  if (val) {
                    setSelectedDateType(val);
                  }
                }}
              />
            </FilterField>

            <FilterField label="Van">
              <Input type="date" value={startDate} onChange={setStartDate} />
            </FilterField>

            <FilterField label="Tot">
              <Input type="date" value={endDate} onChange={setEndDate} />
            </FilterField>

            <FilterField label="Order status">
              <SimpleSelect
                selectedItem={selectedOrderStatus}
                items={[...ORDER_STATUS_OPTIONS]}
                isOptional={true}
                onSelect={(val) => {
                  if (val?.key === selectedOrderStatus?.key) {
                    setSelectedOrderStatus(null);
                  } else {
                    setSelectedOrderStatus(val);
                  }
                }}
              />
            </FilterField>

            <FilterField label="Verkoop status">
              <SimpleSelect
                selectedItem={selectedSaleStatus}
                items={[...SALE_STATUS_OPTIONS]}
                isOptional={true}
                onSelect={(val) => {
                  if (val?.key === selectedSaleStatus?.key) {
                    setSelectedSaleStatus(null);
                  } else {
                    setSelectedSaleStatus(val);
                  }
                }}
              />
            </FilterField>

            <FilterField label="Aankoop status">
              <SimpleSelect
                selectedItem={selectedPurchaseStatus}
                items={[...PURCHASE_STATUS_OPTIONS]}
                isOptional={true}
                onSelect={(val) => {
                  if (val?.key === selectedPurchaseStatus?.key) {
                    setSelectedPurchaseStatus(null);
                  } else {
                    setSelectedPurchaseStatus(val);
                  }
                }}
              />
            </FilterField>

            <FilterField label="Type carrier">
              <SimpleSelect
                selectedItem={selectedCarrierType}
                items={[...CARRIER_TYPE_OPTIONS]}
                isOptional={true}
                onSelect={(val) => {
                  if (val?.key === selectedCarrierType?.key) {
                    setSelectedCarrierType(null);
                  } else {
                    setSelectedCarrierType(val);
                  }
                }}
              />
            </FilterField>

            <FilterField label="Documenten">
              <DocumentFilterSelect
                selectedKeys={selectedDocFilters}
                onChange={(keys) => setSelectedDocFilters(keys)}
              />
            </FilterField>

            <OrderLocationCombobox
              orderLineStopType={OrderLineStopType.Load}
              labelText="Laad locatie"
              value={loadLocationFilter}
              hideInvalid={true}
              isNullable={true}
              onChange={(val) => {
                setLoadLocationFilter(val);
              }}
            />

            <OrderLocationCombobox
              orderLineStopType={OrderLineStopType.Unload}
              labelText="Los locatie"
              value={unloadLocationFilter}
              hideInvalid={true}
              isNullable={true}
              onChange={(val) => {
                setUnloadLocationFilter(val);
              }}
            />
          </FilterPanel>
        }
        summary={`Totaal aantal orders: ${totalOrderCount}`}
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={ORDERS_HEADERS}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          mapData={(order) => {
            const orderStatusColor = ORDER_STATUS_COLOR[order.status] ?? 'neutral';
            const orderStatusText = ORDER_STATUS_OPTIONS.find((i) => i.key === order.status)?.name ?? order.status;
            const purchaseStatusColor = PURCHASE_STATUS_COLOR[order.purchaseStatus] ?? 'neutral';
            const purchaseStatusText =
              PURCHASE_STATUS_OPTIONS.find((i) => i.key === order.purchaseStatus)?.name ?? order.purchaseStatus;
            const saleStatusColor = SALE_STATUS_COLOR[order.saleStatus] ?? 'neutral';
            const saleStatusText =
              SALE_STATUS_OPTIONS.find((i) => i.key === order.saleStatus)?.name ?? order.saleStatus;
            const loadStops = order.lines.map((v) => v.stops.filter((s) => s.type === OrderLineStopType.Load)).flat();
            const unloadStops = order.lines
              .map((v) => v.stops.filter((s) => s.type === OrderLineStopType.Unload))
              .flat();
            const loadDates = Array.from(new Set(loadStops.map((s) => formatDate(s.date)))).join(', ');
            const unloadDates = Array.from(new Set(unloadStops.map((s) => formatDate(s.date)))).join(', ');
            const loadTimes = Array.from(new Set(loadStops.map((s) => formatInputTime(s.timeStart)))).join(', ');
            const unloadTimes = Array.from(new Set(unloadStops.map((s) => formatInputTime(s.timeEnd)))).join(', ');
            const lineSales = order.lines.map((l) => l.sales).flat();
            const linePurchases = order.lines.map((l) => l.purchases).flat();
            const saleTotal = calculateLinesTotalExclVat(lineSales);
            const purchaseTotal = calculateLinesTotalExclVat(linePurchases);

            return [
              <Link to={`${order.id}/general`} className="link-text whitespace-nowrap">
                {order.orderNumber ?? 'DRAFT'}
              </Link>,
              order.customer?.name ?? '-',
              order.customerRef,
              [
                ...new Set(
                  order.lines
                    .map((l) =>
                      l.stops
                        .filter((s) => s.type === OrderLineStopType.Load)
                        .map((s) => s.location.city)
                        .flat(),
                    )
                    .flat(),
                ),
              ].join(', '),
              [
                ...new Set(
                  order.lines
                    .map((l) =>
                      l.stops
                        .filter((s) => s.type === OrderLineStopType.Unload)
                        .map((s) => s.location.city)
                        .flat(),
                    )
                    .flat(),
                ),
              ].join(', '),
              <div className="whitespace-nowrap">{`€ ${decimateAndFormatNumber(purchaseTotal, 2, {
                decimalSeperator: ',',
              })}`}</div>,
              <div className="whitespace-nowrap">{`€ ${decimateAndFormatNumber(saleTotal, 2, {
                decimalSeperator: ',',
              })}`}</div>,
              <Pill color={orderStatusColor} size="sm">
                {orderStatusText}
              </Pill>,
              <Pill color={purchaseStatusColor} size="sm">
                {purchaseStatusText}
              </Pill>,
              <Pill color={saleStatusColor} size="sm">
                {saleStatusText}
              </Pill>,
              order.documentValidationStatus === OrderDocumentValidationStatus.Approved ? (
                <CheckIcon className="h-5 w-5 text-positive-text" />
              ) : (
                <XIcon className="h-5 w-5 text-negative-text" />
              ),
              loadDates,
              loadTimes,
              unloadDates,
              unloadTimes,
              order.supplier?.name ?? '',
              order.supplier == null ? (
                '-'
              ) : order.supplier.regularCarrier ? (
                <Pill color="positive" size="sm">
                  Vast
                </Pill>
              ) : (
                <Pill color="info" size="sm">
                  Spot
                </Pill>
              ),
            ];
          }}
        />
      </ListPage>
    </>
  );
};

export default OrdersPage;
